/** Same-tab Resource recovery. A saved Resource and its return/link outcome are
 * independent. No command runs on load; original commands are never rebuilt. */
import { z } from 'zod';
import { isCurrentDraftContext, resourceContinuationPrefix, type DraftContext } from '@/features/coach-review/composerDrafts';
import { resourceSaveBodySchema as saveBody } from './resourceEditorPayload';
import { captureResourceCommand } from './resourceCommands';
import { feedbackProseLength } from './feedbackAuthoringContinuation';
import type { ResourceCommand, ResourceCommandResult, ResourceKind } from './types';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
const version = z.number().int().min(1).max(2147483647);
const label = z.string().refine(value => feedbackProseLength(value) <= 160);
const common = { operation_id: uuid, expected_version: version };
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const invalid = () => new Error('resource_continuation_invalid');
const unavailable = () => new DOMException('Resource editor context changed', 'AbortError');
const blocked = () => new Error('resource_continuation_requires_original_reconciliation');
const storageFailure = () => new Error('resource_continuation_storage_unavailable');

const commandSchema = z.discriminatedUnion('action',[
  z.object({ action:z.literal('create'), body:saveBody.refine(body => body.expected_version === 0) }).strict(),
  // Publication state is honoured on create only; a save never carries one.
  z.object({ action:z.literal('save'), resourceId:uuid, body:saveBody.refine(body => body.expected_version > 0
    && body.publication_state == null) }).strict(),
  z.object({ action:z.literal('metadata'), resourceId:uuid, body:z.object({ ...common,
    schema_version:z.literal('resource-metadata.v1'), label }).strict() }).strict(),
  z.object({ action:z.literal('duplicate'), resourceId:uuid, body:z.object({ ...common,
    schema_version:z.literal('resource-duplicate.v1'), revision_id:uuid }).strict() }).strict(),
  z.object({ action:z.literal('archive'), resourceId:uuid, body:z.object({ ...common,
    schema_version:z.literal('resource-archive.v1') }).strict() }).strict(),
  z.object({ action:z.literal('publish'), resourceId:uuid, body:z.object({ ...common,
    schema_version:z.literal('resource-publish.v1') }).strict() }).strict(),
]);
const targetSchema = z.discriminatedUnion('kind',[
  z.object({ kind:z.literal('standalone') }).strict(),
  z.object({ kind:z.literal('feedback'), entry_id:uuid, creation_attempt_id:uuid }).strict(),
]);
export type ResourceEditorReturnTarget = z.infer<typeof targetSchema>;
const receiptSchema = z.object({ schema_version:z.literal('resource-result.v1'), resource_id:uuid,
  revision_id:uuid, version }).strict();
const selectionSchema = z.object({ resource_id:uuid,revision_id:uuid,version }).strict();
export type ResourceEditorSelection = z.infer<typeof selectionSchema>;
const recordSchema = z.object({ schema_version:z.literal('resource-editor.v1'), entry_id:uuid,
  resource_kind:z.enum(['tactical_board','moment','clip']).optional(),
  return_target:targetSchema, attempt:z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  origin:selectionSchema.nullable(),
  pending:commandSchema.nullable(), accepted:z.object({ command:commandSchema, receipt:receiptSchema,
    link_complete:z.boolean() }).strict().nullable() }).strict();
export type ResourceEditorContinuation = {
  schema_version:'resource-editor.v1'; entry_id:string; resource_kind?:ResourceKind; return_target:ResourceEditorReturnTarget; attempt:number;
  origin:ResourceEditorSelection | null;
  pending:ResourceCommand | null;
  accepted:{ command:ResourceCommand; receipt:ResourceCommandResult; link_complete:boolean } | null;
};

/** Legacy rows were board-only. A retained content command can also identify
 * its own kind; new metadata-only recording operations always persist the tag. */
export function resourceEditorContinuationKind(row:ResourceEditorContinuation):ResourceKind {
  const commands=[row.pending,row.accepted?.command];
  const content=commands.find(command=>command?.action==='create'||command?.action==='save');
  const inferred=content&&(content.action==='create'||content.action==='save')?content.body.kind:'tactical_board';
  const kind=row.resource_kind??inferred;
  if(commands.some(command=>(command?.action==='create'||command?.action==='save')&&command.body.kind!==kind))throw invalid();
  return kind;
}
function decode(raw: string, entryId: string): ResourceEditorContinuation {
  // Two maximum board snapshots may coexist (prior acceptance + pending save):
  // 40 shapes × 200 points each, plus bounded labels/IDs. Keep room for both;
  // storage quota failure still blocks dispatch instead of dropping recovery.
  if (raw.length > 2 * 1024 * 1024) throw invalid();
  const value: unknown = JSON.parse(raw);
  if (!recordSchema.safeParse(value).success || (value as ResourceEditorContinuation).entry_id !== entryId) throw invalid();
  resourceEditorContinuationKind(value as ResourceEditorContinuation);
  return value as ResourceEditorContinuation;
}
function key(context: DraftContext, entryId: string): string {
  const prefix = resourceContinuationPrefix(context);
  if (!prefix) throw unavailable();
  if (!uuid.safeParse(entryId).success) throw invalid();
  return prefix + entryId.toLowerCase();
}
export function loadResourceEditorContinuation(context: DraftContext, entryId: string): ResourceEditorContinuation | null {
  const storageKey = key(context,entryId);
  let raw: string | null;
  try { raw = sessionStorage.getItem(storageKey); } catch { throw storageFailure(); }
  return raw === null ? null : decode(raw,entryId.toLowerCase());
}
export function listResourceEditorContinuations(context: DraftContext): ResourceEditorContinuation[] {
  const prefix = resourceContinuationPrefix(context);
  if (!prefix) throw unavailable();
  const keys: string[] = [];
  try {
    for (let i=0;i<sessionStorage.length;i++) {
      const candidate = sessionStorage.key(i);
      if (candidate?.startsWith(prefix)) keys.push(candidate.slice(prefix.length));
    }
  } catch { throw storageFailure(); }
  return keys.sort().flatMap(id => {
    let raw: string | null;
    try { raw = sessionStorage.getItem(prefix + id); } catch { throw storageFailure(); }
    if (raw === null) return [];
    // A row this build cannot read (one written for a retired return target,
    // for example) is not listed; opening its entry directly still fails closed.
    try { const row = decode(raw, id.toLowerCase()); return row.pending || row.accepted ? [row] : []; }
    catch { return []; }
  });
}
function write(context: DraftContext, row: ResourceEditorContinuation): void {
  const storageKey = key(context,row.entry_id), raw = JSON.stringify(row);
  decode(raw,row.entry_id);
  try {
    sessionStorage.setItem(storageKey,raw);
    if (sessionStorage.getItem(storageKey) !== raw) throw storageFailure();
  } catch { throw storageFailure(); }
}
function newRow(entryId:string, target:ResourceEditorReturnTarget, origin:ResourceEditorSelection|null,kind:ResourceKind):ResourceEditorContinuation {
  return { schema_version:'resource-editor.v1',entry_id:entryId.toLowerCase(),return_target:clone(target),resource_kind:kind,
    origin:clone(origin),attempt:0,pending:null,accepted:null };
}
/** Persist an empty return entry before navigation/source work. A missing
 * original journal must never be interpreted as an unsent operation. */
export function initializeResourceEditorContinuation(context:DraftContext,entryId:string,
  target:ResourceEditorReturnTarget,kind:ResourceKind):void {
  if(!targetSchema.safeParse(target).success||!['tactical_board','moment','clip'].includes(kind))throw invalid();
  key(context,entryId);
  const row=loadResourceEditorContinuation(context,entryId);
  if(row){
    if(!same(row.return_target,target)||resourceEditorContinuationKind(row)!==kind)throw blocked();
    return;
  }
  write(context,newRow(entryId,target,null,kind));
}

/** A second mounted controller cannot replace or reclassify an existing attempt. */
export function continueResourceEditorCommand(context:DraftContext, entryId:string,
  returnTarget:ResourceEditorReturnTarget, command:ResourceCommand,
  options:{restoredUnknown?:boolean;origin?:ResourceEditorSelection|null;kind?:ResourceKind}={}) {
  const captured=clone(command), target=clone(returnTarget), origin=clone(options.origin ?? null);
  const kind=options.kind??((captured.action==='create'||captured.action==='save')?captured.body.kind:'tactical_board');
  if(!['tactical_board','moment','clip'].includes(kind)||(captured.action==='create'||captured.action==='save')&&captured.body.kind!==kind)throw invalid();
  if (!commandSchema.safeParse(captured).success || !targetSchema.safeParse(target).success) throw invalid();
  if (captured.action==='create' ? origin!==null : !selectionSchema.safeParse(origin).success
    || captured.resourceId!==origin?.resource_id || captured.body.expected_version!==origin?.version
    || captured.action==='duplicate' && captured.body.revision_id!==origin?.revision_id) throw invalid();
  key(context,entryId);
  const controller=captureResourceCommand(context,captured,{restoredUnknown:options.restoredUnknown});
  let attempt:number|null=null, inFlight:Promise<ResourceCommandResult>|null=null;
  function ownRow():ResourceEditorContinuation {
    const row=loadResourceEditorContinuation(context,entryId) ?? newRow(entryId,target,origin,kind);
    if (!same(row.return_target,target)||resourceEditorContinuationKind(row)!==kind) throw blocked();
    return {...row,resource_kind:kind};
  }
  return {
    getState:()=>isCurrentDraftContext(context) ? controller.getState() : {status:'unavailable' as const},
    getIntent:()=>isCurrentDraftContext(context) ? clone(captured) : null,
    releaseRejected():void {
      if (controller.getState().status !== 'rejected') throw blocked();
      const row=ownRow();
      if (row.attempt!==attempt || !same(row.pending,captured)) throw blocked();
      write(context,{...row,pending:null});
    },
    send(signal?:AbortSignal):Promise<ResourceCommandResult> {
      if (!isCurrentDraftContext(context) || signal?.aborted) return Promise.reject(unavailable());
      if (inFlight) return inFlight;
      try {
        const row=ownRow();
        if (row.accepted && same(row.accepted.command,captured) && row.pending===null) {
          // A persisted acknowledgment does not require another POST. It also
          // does not grant current source access; link admission re-reads it.
          return Promise.resolve(clone(row.accepted.receipt));
        }
        if (row.pending && (!same(row.pending,captured) || !same(row.origin,origin))) throw blocked();
        if (row.accepted && row.return_target.kind!=='standalone') throw blocked();
        if (controller.getState().status!=='accepted') {
          const ownedRejection=row.pending && row.attempt===attempt && controller.getState().status==='rejected';
          attempt=row.attempt+1;
          if (row.pending && !ownedRejection) controller.adoptUnresolved();
          write(context,{...row,origin,attempt,pending:captured});
        }
      } catch(error) { return Promise.reject(error); }
      inFlight=controller.send(signal).then(receipt=>{
        if (!isCurrentDraftContext(context)) throw unavailable();
        const row=ownRow();
        if (row.attempt!==attempt || !same(row.pending,captured)) throw blocked();
        // A storage failure leaves the original pending journal. This mounted
        // controller still knows acceptance; retry persists it without POST.
        write(context,{...row,pending:null,accepted:{command:captured,receipt,link_complete:false}});
        return clone(receipt);
      }).finally(()=>{inFlight=null;});
      return inFlight;
    },
  };
}

export function restoreResourceEditorCommand(context:DraftContext, entryId:string) {
  const row=loadResourceEditorContinuation(context,entryId);
  return row?.pending ? continueResourceEditorCommand(context,entryId,row.return_target,row.pending,
    {restoredUnknown:true,origin:row.origin,kind:resourceEditorContinuationKind(row)}) : null;
}
/** Call only after this original target has explicitly admitted the exact pin. */
export function completeResourceEditorLink(context:DraftContext, entryId:string,
  target:ResourceEditorReturnTarget, receipt:ResourceCommandResult):void {
  const row=loadResourceEditorContinuation(context,entryId);
  if (!row?.accepted || row.pending || !same(row.return_target,target) || !same(row.accepted.receipt,receipt)) throw blocked();
  write(context,{...row,accepted:{...row.accepted,link_complete:true}});
}
/** Explicit Resource-first "Create feedback" chooses its return entry once.
 * Persist this identity before opening/admitting into the existing composer. */
export function bindResourceEditorReturn(context:DraftContext,entryId:string,
  target:Extract<ResourceEditorReturnTarget,{kind:'feedback'}>):void {
  if(!targetSchema.safeParse(target).success)throw invalid();
  const row=loadResourceEditorContinuation(context,entryId);
  if(!row?.accepted || row.pending || row.accepted.link_complete
    || row.return_target.kind!=='standalone')throw blocked();
  write(context,{...row,return_target:clone(target)});
}
/** Explicit local dismissal never deletes the independent Resource. Unknown
 * commands cannot be dismissed; preserve a high-water attempt for late writers. */
export function dismissResourceEditorContinuation(context:DraftContext,entryId:string):void {
  const row=loadResourceEditorContinuation(context,entryId);
  if (!row) return;
  if (row.pending) throw blocked();
  write(context,{...row,accepted:null});
}
